import { ReactElement, useState, useEffect } from 'react';

import { collectionGroup, getDocs, query, updateDoc, where } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import PersonAutocomplete from '../components/person/PersonAutocomplete';
import { useAuth } from '../contextProviders/AuthProvider';
import usePersons from '../dataHooks/usePersons';
import useSupportAction from '../dataHooks/useSupportAction';
import useSystemErrors from '../dataHooks/useSystemErrors';
import useUserActions from '../dataHooks/useUserActions';
import { usePersonMutators } from '../dataMutators/usePersonMutators';
import { useSupportActionMutators } from '../dataMutators/useSupportActionMutators';
import { ORG_MEMBER_TYPES } from '../enums/OrgMemberType';
import { firestore } from '../firebase';
import { MEMBERSHIP_STATUSES } from '../types/Membership';
import Person, { PERSON_STATUSES } from '../types/Person';
import { SupportActionStatus, SUPPORT_ACTION_STATUSES, SUPPORT_ACTION_TYPES } from '../types/SupportAction';
import { SYSTEM_ERROR_CATEGORIES, SystemErrorCategory } from '../types/SystemError';
import { UserActionType, USER_ACTION_TYPES } from '../types/UserAction';
import { parseGhinHandicapIndex, HANDICAP_LABELS } from '../utils/handicapUtils';

const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const USER_SUPPORT_TABS = {
  SUPPORT: 'support',
  ACTIVITY: 'activity',
} as const;

type UserSupportTab = typeof USER_SUPPORT_TABS[keyof typeof USER_SUPPORT_TABS];

function getActionTypeColor(actionType: UserActionType): 'success' | 'error' | 'info' | 'warning' | 'default' {
  switch (actionType) {
    case USER_ACTION_TYPES.PROFILE_CREATED:
    case USER_ACTION_TYPES.MEMBERSHIP_ACTIVATED:
    case USER_ACTION_TYPES.APPLICATION_FULFILLED:
    case USER_ACTION_TYPES.INVITATION_CREATED:
      return 'success';
    case USER_ACTION_TYPES.PROFILE_DELETED:
    case USER_ACTION_TYPES.AUTH_DELETED:
    case USER_ACTION_TYPES.MEMBERSHIP_REMOVED:
    case USER_ACTION_TYPES.INVITATION_DELETED:
      return 'error';
    case USER_ACTION_TYPES.APPLICATION_CREATED:
      return 'info';
    case USER_ACTION_TYPES.EMAIL_CONFLICT_FLAGGED:
      return 'warning';
    default:
      return 'default';
  }
}

function timestampToDate(createTime: unknown): Date | null {
  if (!createTime) return null;
  const ts = createTime as { toDate?: () => Date };
  if (ts.toDate) return ts.toDate();
  return null;
}

const ACTIVITY_SOURCE = {
  USER_ACTIONS: 'userActions',
  SYSTEM_ERRORS: 'systemErrors',
} as const;
type ActivitySource = typeof ACTIVITY_SOURCE[keyof typeof ACTIVITY_SOURCE];

const SYSTEM_ERROR_CATEGORY_LABELS: Record<string, string> = {
  USER_SETUP: 'User Setup',
  EVENT_REGISTRATION: 'Event Reg',
  EVENT_CREATION: 'Event Creation',
};

const activityColumns: GridColDef[] = [
  {
    field: 'createTime',
    headerName: 'Date / Time',
    flex: 1,
    minWidth: 150,
    valueGetter: (value: unknown) => timestampToDate(value),
    valueFormatter: (value: Date | null) => value ? value.toLocaleString() : '',
  },
  {
    field: 'actionType',
    headerName: 'Type',
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        label={params.value}
        color={getActionTypeColor(params.value)}
        size="small"
      />
    ),
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 1.2,
    minWidth: 180,
  },
  {
    field: 'orgName',
    headerName: 'Org',
    flex: 1,
    minWidth: 120,
    valueFormatter: (value: string | null) => value || '',
  },
  {
    field: 'summary',
    headerName: 'Comment',
    flex: 1.5,
    minWidth: 200,
  },
];

const systemErrorColumns: GridColDef[] = [
  {
    field: 'createTime',
    headerName: 'Date / Time',
    flex: 1,
    minWidth: 150,
    valueGetter: (value: unknown) => timestampToDate(value),
    valueFormatter: (value: Date | null) => value ? value.toLocaleString() : '',
  },
  {
    field: 'category',
    headerName: 'Category',
    flex: 0.9,
    minWidth: 130,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        label={SYSTEM_ERROR_CATEGORY_LABELS[params.value] ?? params.value}
        color={params.value === 'USER_SETUP' ? 'warning' : params.value === 'EVENT_REGISTRATION' ? 'info' : 'default'}
        size="small"
      />
    ),
  },
  {
    field: 'errorCode',
    headerName: 'Error',
    flex: 1.2,
    minWidth: 180,
    renderCell: (params: GridRenderCellParams) => (
      <Chip label={params.value} color="error" size="small" variant="outlined" />
    ),
  },
  {
    field: 'resolved',
    headerName: 'Status',
    flex: 0.6,
    minWidth: 90,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        label={params.value ? 'Resolved' : 'Open'}
        color={params.value ? 'success' : 'error'}
        size="small"
      />
    ),
  },
  {
    field: 'context',
    headerName: 'Email',
    flex: 1.2,
    minWidth: 180,
    valueGetter: (value: Record<string, string | number | boolean | null> | undefined) =>
      value?.email ?? '',
  },
  {
    field: 'summary',
    headerName: 'Details',
    flex: 1.5,
    minWidth: 200,
  },
];

export default function UserSupportPage(): ReactElement {
  const { isGod } = useAuth();
  const [tabValue, setTabValue] = useState<UserSupportTab>(USER_SUPPORT_TABS.SUPPORT);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [syncAllProgress, setSyncAllProgress] = useState<{ current: number; total: number } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [activityEmailFilter, setActivityEmailFilter] = useState('');
  const [activitySource, setActivitySource] = useState<ActivitySource>(ACTIVITY_SOURCE.USER_ACTIONS);
  const [errorCategoryFilter, setErrorCategoryFilter] = useState<string | null>(null);
  const [showUnresolvedOnly, setShowUnresolvedOnly] = useState(true);
  const { createSupportAction } = useSupportActionMutators();
  const { updatePerson } = usePersonMutators();
  const { supportActions, isSupportActionsLoading, supportActionsError } = useSupportAction({
    personId: selectedPerson?.itemId || null,
  });

  // Activity log
  const { userActions, isUserActionsLoading, userActionsError } = useUserActions({
    email: activityEmailFilter || null,
  });

  // System errors
  const { systemErrors, isSystemErrorsLoading, systemErrorsError } = useSystemErrors({
    category: errorCategoryFilter as SystemErrorCategory | null,
    unresolvedOnly: showUnresolvedOnly,
  });

  // Get all persons for the autocomplete and for sync all
  // Use membershipStatus array to get active and pending persons
  const { persons: allPersons, isPersonsLoading: isAllPersonsLoading, personsError: allPersonsError } = usePersons({
    membershipStatus: [MEMBERSHIP_STATUSES.ACTIVE, MEMBERSHIP_STATUSES.PENDING],
    orgMemberType: ORG_MEMBER_TYPES.MEMBER,
  });

  // Create a hook that wraps usePersons to match PeopleHookResult
  const usePeopleHook = () => {

    useEffect(() => {
      return () => {
        // This empty effect ensures proper cleanup of the useSupportAction hook
        // when the component unmounts or when selectedPerson changes
      };
    }, []);

    return {
      items: allPersons || [],
      isLoading: isAllPersonsLoading,
      error: allPersonsError || undefined,
    };
  };

  // Cleanup effect for useSupportAction
  useEffect(() => {
    return () => {
      // This empty effect ensures proper cleanup of the useSupportAction hook
      // when the component unmounts or when selectedPerson changes
    };
  }, [selectedPerson, supportActions, isSupportActionsLoading, supportActionsError]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: UserSupportTab) => {
    setTabValue(newValue);
  };

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;
    setNewEmail(email);
    validateEmail(email);
  };

  const handleVerifyEmail = async () => {
    if (!selectedPerson) return;
    await createSupportAction(selectedPerson.itemId, SUPPORT_ACTION_TYPES.VERIFY_EMAIL);
  };

  const handleChangeEmail = async () => {
    if (!selectedPerson) return;
    if (!validateEmail(newEmail)) return;

    await createSupportAction(selectedPerson.itemId, SUPPORT_ACTION_TYPES.CHANGE_EMAIL, [newEmail]);
    setNewEmail('');
  };

  const handleDeleteProfile = async () => {
    if (!selectedPerson) return;
    await updatePerson({ itemId: selectedPerson.itemId, status: PERSON_STATUSES.DELETED });
    setConfirmingDelete(false);
    setSelectedPerson(null);
  };

  const handleSyncEmail = async () => {
    if (!selectedPerson) return;

    setIsSyncing(true);
    setSyncMessage(null);

    try {
      // Query all memberships for this person
      const membershipQuery = query(
        collectionGroup(firestore, 'membership'),
        where('itemId', '==', selectedPerson.itemId)
      );
      const membershipDocs = await getDocs(membershipQuery);

      // Convert ghin index string to number
      const ghinIndexNumber = selectedPerson.index ? parseGhinHandicapIndex(selectedPerson.index) : undefined;

      // Create all update promises
      const updatePromises = membershipDocs.docs.map(async (membershipDoc) => {
        const membershipRef = membershipDoc.ref;

        const updateData: Record<string, string | number> = {
          email: selectedPerson.email
        };

        // Only update GHIN fields if they exist on the person
        if (selectedPerson.ghin !== undefined) {
          updateData.ghin = selectedPerson.ghin;
        }
        if (ghinIndexNumber !== undefined) {
          updateData.ghinIndex = ghinIndexNumber;
        }

        try {
          await updateDoc(membershipRef, updateData);
          return { success: true };
        } catch (error) {
          console.error(`Failed to update membership ${membershipDoc.ref.path}:`, error);
          return { success: false };
        }
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;

      if (errorCount === 0) {
        setSyncMessage({
          text: `Successfully synced email and ${HANDICAP_LABELS.ID} to ${successCount} organization${successCount !== 1 ? 's' : ''}`,
          type: 'success'
        });
      } else {
        setSyncMessage({
          text: `Synced to ${successCount} orgs, failed for ${errorCount} orgs`,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error syncing email and GHIN:', error);
      setSyncMessage({ text: `Failed to sync email and ${HANDICAP_LABELS.ID}`, type: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusColor = (status: SupportActionStatus) => {
    switch (status) {
      case SUPPORT_ACTION_STATUSES.RESOLVED:
        return 'success.main';
      case SUPPORT_ACTION_STATUSES.ERROR:
        return 'error.main';
      case SUPPORT_ACTION_STATUSES.PENDING:
        return 'warning.main';
      default:
        return 'text.primary';
    }
  };

  const handleSyncAllEmails = async () => {
    if (!allPersons || allPersons.length === 0) {
      setSyncMessage({ text: 'No users found to sync', type: 'error' });
      return;
    }

    setIsSyncingAll(true);
    setSyncMessage(null);
    setSyncAllProgress({ current: 0, total: allPersons.length });

    let processedUsers = 0;
    let totalSuccessCount = 0;
    let totalErrorCount = 0;

    try {
      for (const person of allPersons) {
        // Update progress
        processedUsers++;
        setSyncAllProgress({ current: processedUsers, total: allPersons.length });

        // Query all memberships for this person
        const membershipQuery = query(
          collectionGroup(firestore, 'membership'),
          where('itemId', '==', person.itemId)
        );
        const membershipDocs = await getDocs(membershipQuery);

        // Convert ghin index string to number
        const ghinIndexNumber = person.index ? parseGhinHandicapIndex(person.index) : undefined;

        // Update each membership for this person
        const updatePromises = membershipDocs.docs.map(async (membershipDoc) => {
          const membershipRef = membershipDoc.ref;

          const updateData: Record<string, string | number> = {
            email: person.email
          };

          // Only update GHIN fields if they exist on the person
          if (person.ghin !== undefined) {
            updateData.ghin = person.ghin;
          }
          if (ghinIndexNumber !== undefined) {
            updateData.ghinIndex = ghinIndexNumber;
          }

          try {
            await updateDoc(membershipRef, updateData);
            return { success: true };
          } catch (error) {
            console.error(`Failed to update membership ${membershipDoc.ref.path}:`, error);
            return { success: false };
          }
        });

        const results = await Promise.all(updatePromises);
        totalSuccessCount += results.filter(r => r.success).length;
        totalErrorCount += results.filter(r => !r.success).length;
      }

      setSyncAllProgress(null);

      if (totalErrorCount === 0) {
        setSyncMessage({
          text: `Successfully synced ${processedUsers} users across ${totalSuccessCount} memberships`,
          type: 'success'
        });
      } else {
        setSyncMessage({
          text: `Synced ${processedUsers} users: ${totalSuccessCount} memberships succeeded, ${totalErrorCount} failed`,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error syncing all emails and GHINs:', error);
      setSyncMessage({ text: `Failed to sync all emails and ${HANDICAP_LABELS.ID}s`, type: 'error' });
      setSyncAllProgress(null);
    } finally {
      setIsSyncingAll(false);
    }
  };

  return (
    <GratiPageWrapper
      isAuthRequired={true}
      isNoAuthRequired={false}
      validateUser={() => isGod}
      isContentLoading={false}
      contentError={supportActionsError}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="headlineLarge" sx={{ mb: 2 }}>
          User Support
        </Typography>

        <TabContext value={tabValue}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Support" value={USER_SUPPORT_TABS.SUPPORT} />
            <Tab label="Activity" value={USER_SUPPORT_TABS.ACTIVITY} />
          </Tabs>

          <TabPanel value={USER_SUPPORT_TABS.SUPPORT} sx={{ px: 0 }}>
            <Stack
              direction="row"
              sx={{
                justifyContent: "flex-end",
                mb: 2
              }}>
              {isGod && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleSyncAllEmails}
                  disabled={isSyncingAll || isAllPersonsLoading}
                  sx={{ width: 'fit-content' }}
                >
                  {isSyncingAll ? (
                    <Stack direction="row" spacing={1} sx={{
                      alignItems: "center"
                    }}>
                      <CircularProgress size={20} />
                      {syncAllProgress && (
                        <span>{syncAllProgress.current}/{syncAllProgress.total}</span>
                      )}
                    </Stack>
                  ) : (
                    'Sync All Users'
                  )}
                </Button>
              )}
            </Stack>

            <Stack spacing={2}>
              <PersonAutocomplete<Person>
                label="Select User"
                value={selectedPerson}
                usePeopleHook={usePeopleHook}
                onChange={(_, value) => {
                  setSelectedPerson(value);
                  setSyncMessage(null);
                  setConfirmingDelete(false);
                }}
              />

              {selectedPerson && (
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" onClick={handleVerifyEmail} sx={{ width: 'fit-content' }}>
                      Verify Email
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSyncEmail}
                      disabled={isSyncing}
                      sx={{ width: 'fit-content' }}
                    >
                      {isSyncing ? <CircularProgress size={20} /> : `Sync Email & ${HANDICAP_LABELS.ID}`}
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{
                    alignItems: "flex-start"
                  }}>
                    <TextField label="New Email" value={newEmail} onChange={handleEmailChange} error={!!emailError} />
                    <Button
                      variant="contained"
                      onClick={handleChangeEmail}
                      disabled={!newEmail || !!emailError}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      Change email
                    </Button>
                  </Stack>

                  {/* Delete Profile — inline confirmation */}
                  <Stack direction="row" spacing={2} sx={{
                    alignItems: "center"
                  }}>
                    {!confirmingDelete ? (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setConfirmingDelete(true)}
                        sx={{ width: 'fit-content' }}
                      >
                        Delete Profile
                      </Button>
                    ) : (
                      <>
                        <Typography variant="bodyMedium" sx={{
                          color: "error.main"
                        }}>
                          Delete {selectedPerson.name}? This will also delete their auth account.
                        </Typography>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleDeleteProfile}
                          sx={{ width: 'fit-content' }}
                        >
                          Confirm Delete
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => setConfirmingDelete(false)}
                          sx={{ width: 'fit-content' }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </Stack>

                  {syncMessage && (
                    <Typography
                      variant="bodyMedium"
                      color={syncMessage.type === 'success' ? 'success.main' : 'error.main'}
                    >
                      {syncMessage.text}
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>

            {selectedPerson && isSupportActionsLoading && <CircularProgress sx={{ mt: 2 }} />}

            {selectedPerson && supportActions.length > 0 && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Typography variant="headlineSmall" gutterBottom>
                  Recent actions
                </Typography>

                {supportActions.map((action) => (
                  <Box
                    key={action.itemId}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: getStatusColor(action.status),
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="bodyLarge">Action: {action.action}</Typography>
                    <Typography variant="bodyLarge" color={getStatusColor(action.status)}>Status: {action.status}</Typography>
                    {action.parameters && action.parameters.length > 0 && (
                      <Typography variant="bodyMedium" sx={{
                        color: "text.secondary"
                      }}>New Email: {action.parameters[0]}</Typography>
                    )}
                    {action.history.length > 0 && (
                      <Typography variant="bodyMedium" sx={{
                        color: "text.secondary"
                      }}>
                        Last Update: {action.history[action.history.length - 1].comment}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </TabPanel>

          <TabPanel value={USER_SUPPORT_TABS.ACTIVITY} sx={{ px: 0 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={activitySource}
                  exclusive
                  onChange={(_e, val) => { if (val) setActivitySource(val); }}
                  size="small"
                >
                  <ToggleButton value={ACTIVITY_SOURCE.USER_ACTIONS}>User Actions</ToggleButton>
                  <ToggleButton value={ACTIVITY_SOURCE.SYSTEM_ERRORS}>System Errors</ToggleButton>
                </ToggleButtonGroup>

                {activitySource === ACTIVITY_SOURCE.USER_ACTIONS && (
                  <TextField
                    label="Filter by email"
                    value={activityEmailFilter}
                    onChange={(e) => setActivityEmailFilter(e.target.value)}
                    size="small"
                    sx={{ maxWidth: '25rem' }}
                  />
                )}

                {activitySource === ACTIVITY_SOURCE.SYSTEM_ERRORS && (
                  <>
                    <ToggleButtonGroup
                      value={errorCategoryFilter}
                      exclusive
                      onChange={(_e, val) => setErrorCategoryFilter(val || null)}
                      size="small"
                    >
                      <ToggleButton value="">All</ToggleButton>
                      <ToggleButton value={SYSTEM_ERROR_CATEGORIES.USER_SETUP}>User Setup</ToggleButton>
                      <ToggleButton value={SYSTEM_ERROR_CATEGORIES.EVENT_REGISTRATION}>Event Reg</ToggleButton>
                      <ToggleButton value={SYSTEM_ERROR_CATEGORIES.EVENT_CREATION}>Event Creation</ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup
                      value={showUnresolvedOnly ? 'unresolved' : 'all'}
                      exclusive
                      onChange={(_e, val) => { if (val) setShowUnresolvedOnly(val === 'unresolved'); }}
                      size="small"
                    >
                      <ToggleButton value="unresolved">Open</ToggleButton>
                      <ToggleButton value="all">All</ToggleButton>
                    </ToggleButtonGroup>
                  </>
                )}
              </Stack>

              {activitySource === ACTIVITY_SOURCE.USER_ACTIONS && (
                <>
                  {userActionsError && (
                    <Typography color="error.main">Error loading activity: {userActionsError.message}</Typography>
                  )}
                  <DataGrid
                    rows={userActions}
                    columns={activityColumns}
                    getRowId={(row) => row.itemId}
                    loading={isUserActionsLoading}
                    autoHeight
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    sx={{ '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
                  />
                </>
              )}

              {activitySource === ACTIVITY_SOURCE.SYSTEM_ERRORS && (
                <>
                  {systemErrorsError && (
                    <Typography color="error.main">Error loading system errors: {systemErrorsError.message}</Typography>
                  )}
                  <DataGrid
                    rows={systemErrors}
                    columns={systemErrorColumns}
                    getRowId={(row) => row.itemId}
                    loading={isSystemErrorsLoading}
                    autoHeight
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    sx={{ '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
                  />
                </>
              )}
            </Stack>
          </TabPanel>
        </TabContext>
      </Box>
    </GratiPageWrapper>
  );
}
